import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

/** Backend-relative media paths (e.g. /api/media/prod-x.mp4) -> absolute URLs; external URLs pass through. */
export const mediaUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

/** The nine intelligent hotel features shown in the 45s Hotel Automation film (one 5s scene each). */
export const HOTEL_FEATURES = [
  { hi: "Keyless check-in — फ़ोन ही आपकी चाबी", en: "Keyless Guest Check-in" },
  { hi: "RoboSpark डिजिटल कंसीयर्ज स्वागत में", en: "Digital Concierge · RoboSpark" },
  { hi: "कमरे में कदम — रोशनी जागे, पर्दे खुलें", en: "Welcome Scene Automation" },
  { hi: "Voice AI — 'Good Night' एक शब्द में", en: "Voice AI Night Scenes" },
  { hi: "Smart spa bathroom और mirror display", en: "Smart Spa & Mirror Display" },
  { hi: "RoboSpark room service, चौबीसों घंटे", en: "Robotic Room Service" },
  { hi: "Conference room खुद तैयार — glass, screen, lights", en: "Dynamic Conference Automation" },
  { hi: "Intelligent load shedding — energy की बचत", en: "Intelligent Load Shedding" },
  { hi: "Occupancy analytics और AI security", en: "Occupancy Analytics · AI Security" },
];

const loop = (id, poster) => ({ src: `${API}/media/ad-${id}.mp4`, webm: `${API}/media/ad-${id}.webm`, poster });

/** Silent ambassador loops that replaced the static hero / mart model images. */
export const MEDIA = {
  ambassadorHero: loop("ambassador", `${API}/media/model-hero.png`),
  ambassadorMart: loop("ambassador-mart", `${API}/media/model-mart.png`),
};

/** Autoplay films inside the three pillar cards; captions are synced to scene boundaries (`segment` seconds). */
export const PILLAR_FILMS = {
  township: {
    ...loop("township", `${API}/media/ad-township-1.png`),
    label: "Township Film",
    segment: 10,
    captions: ["Energy Microgrids · Aerial", "AI Surveillance · Rooftop Control", "Perimeter Security · Smart Streets"],
  },
  rooms: {
    ...loop("rooms", `${API}/media/ad-rooms-1.png`),
    label: "Featured Rooms",
    segment: 10,
    captions: ["Living Room · Acoustic Scenes", "Master Suite · Human-centric Lighting", "Kitchen · Voice AI Orchestration"],
  },
  hotelops: {
    ...loop("hotelops", `${API}/media/ad-hotelops-1.png`),
    label: "Hotel Automation · 45s",
    segment: 5,
    captions: HOTEL_FEATURES.map((f) => f.en),
  },
};

/** 30s RoboSpark humanoid showcase for the Mart spotlight strip (poster falls back to a generated robot still). */
export const ROBOSPARK_FILM = {
  ...loop("robospark", `${API}/media/prod-robospark-butler-s1-2.png`),
  label: "RoboSpark Showcase · 30s",
  segment: 10,
  captions: ["Service & Hospitality", "Home Care & Cleaning", "Security & Night Watch"],
};

const film = (id, title, short, poster, subtitles, narrator = null, duration = "0:30", cadence = 7000) => ({
  id,
  title,
  short,
  narrator,
  src: `${API}/media/ad-${id}.mp4`,
  webm: `${API}/media/ad-${id}.webm`,
  master4k: `${API}/media/ad-${id}-4k.mp4`,
  poster: `${API}/media/ad-${id}-${poster}.png`,
  duration,
  cadence,
  subtitles,
});

/** Rotating ad-film series — each film autoplays and hands over to the next. */
export const AD_FILMS = [
  film("palace", "Royal Palace", "Palace", 1, [
    { hi: "LuxeRay Smart Homes", en: "Where royal grandeur meets modern technology" },
    { hi: "जहाँ शाही नवाबी ठाठ, मिलती है सबसे आधुनिक तकनीक से", en: "One touch — and the entire palace obeys you" },
    { hi: "एक छू... और पूरा महल आपके इशारों पर", en: "Lights, security, comfort — all in one touch" },
    { hi: "रोशनी, सुरक्षा, सुख-ओ-आराम — सब कुछ, one touch में", en: "LuxeRay — intelligent living, royal feeling" },
  ], "Matilda"),
  film("hotel", "Smart Hotel Suite", "Hotel", 1, [
    { hi: "LuxeRay Smart Hotels", en: "A royal welcome for every guest" },
    { hi: "कमरे में कदम रखते ही — रोशनी जागे, पर्दे खुलें", en: "Step in — lights wake, drapes open, mood set" },
    { hi: "RoboSpark सेवा में, चौबीसों घंटे", en: "RoboSpark at your service, around the clock" },
    { hi: "Every suite, every moment — intelligently yours", en: "LuxeRay — hospitality, reimagined" },
  ], "Lily"),
  film("township", "Smart Township", "Township", 1, [
    { hi: "LuxeRay Smart Townships", en: "Imagine a city that understands you" },
    { hi: "हर सड़क, हर घर, हर रोशनी — एक ही intelligent network से जुड़ी", en: "Every street, home and light on one intelligent network" },
    { hi: "सुरक्षित, हरा-भरा, और हमेशा आपके साथ", en: "Secure, green, and always with you" },
    { hi: "India's first truly AIoT townships", en: "LuxeRay — the future, built today" },
  ], "Matilda"),
  film("office", "Smart Office", "Office", 1, [
    { hi: "LuxeRay Smart Offices", en: "Where work gets smarter and days get easier" },
    { hi: "रोशनी, तापमान, मीटिंग रूम — सब कुछ अपने आप तैयार", en: "Lighting, climate, meeting rooms — ready on their own" },
    { hi: "Energy saved. Productivity unlocked.", en: "आपका ऑफिस, अब सोचता भी है" },
    { hi: "LuxeRay — Intelligence at work", en: "LuxeRay Smart Homes Pvt Ltd" },
  ], "Sarah"),
  film("hotelops", "Hotel Automation", "Hotel 45s", 1, HOTEL_FEATURES, null, "0:45", 5000),
];

/** Brand voice audition — the palace script in three female voices (ElevenLabs v3, Hindi language enforced).
 *  Matilda is the primary brand voice; each film's narrator is set on the film (`narrator`). */
export const BRAND_VOICE = "matilda";
export const BRAND_VOICES = [
  { id: "matilda", name: "Matilda", tone: "Warm storyteller · deepest pitch (203 Hz)", films: "Royal Palace · Smart Township", src: `${API}/media/ad-palace-voice-matilda.mp3` },
  { id: "lily", name: "Lily", tone: "Refined, elegant narration (216 Hz)", films: "Smart Hotel Suite", src: `${API}/media/ad-palace-voice-lily.mp3` },
  { id: "sarah", name: "Sarah", tone: "Bright, crisp, energetic (225 Hz)", films: "Smart Office", src: `${API}/media/ad-palace-voice-sarah.mp3` },
];
